import { useEffect } from "react";
import CustomImage from "../../atoms/customImage";
import useEggVariantSwitch from "../../../customHooks/pdp/useEggVariantSwitch";

const EGG_VARIANT_ICONS = ["/icons/Eggless.svg", "/icons/with-egg.svg"];

/**
 * Toggle for switching between the egg / eggless variant of a product.
 * Shown on both mobile and desktop; renders nothing if the product has no egg variant.
 *
 * @returns {React.ReactNode}
 */
const EggVariantSwitch = () => {
  const { show, label, isEggless, onToggle } = useEggVariantSwitch();

  // Preload both icons on mount so toggle swaps instantly (no wait for network).
  // Skip entirely when there's no egg variant to show, so we don't fetch icons for nothing.
  useEffect(() => {
    if (!show) return;
    EGG_VARIANT_ICONS.forEach((src) => {
      const image = new window.Image();
      image.src = src;
    });
  }, [show]);

  if (!show) return null;

  const activeColorClass = isEggless ? "bg-[#008A00]" : "bg-[#D05825]";

  return (
    <div className="flex flex-col items-center gap-8">
      <button
        type="button"
        role="switch"
        aria-checked={isEggless}
        aria-label={`${label}. Tap to switch egg variant`}
        onClick={onToggle}
        className={`${activeColorClass} relative h-18 w-[46px] shrink-0 rounded-full`}
      >
        <span
          aria-hidden="true"
          className={`absolute left-0 top-1/2 z-1 flex size-28 -translate-y-1/2 items-center justify-center rounded-full bg-white-900
        shadow-[0_2px_4px_rgba(0,0,0,0.12)] transition-transform duration-300 ease-in-out
        ${
          isEggless
            ? "translate-x-[20px] border-[0.5px] border-[#008A0029]"
            : "-translate-x-[4px] border-[0.5px] border-[#D1482629]"
        }`}
        >
          {/* Keep both icons mounted so the active one is already decoded on toggle. */}
          <CustomImage
            src="/icons/Eggless.svg"
            width={18}
            height={18}
            alt=""
            isPriority={true}
            sizes="18px"
            className={isEggless ? "" : "hidden"}
          />
          <CustomImage
            src="/icons/with-egg.svg"
            width={18}
            height={18}
            alt=""
            isPriority={true}
            sizes="18px"
            className={isEggless ? "hidden" : ""}
          />
        </span>
      </button>

      <span className="text-12 font-500 capitalize text-grey-400">{label}</span>
    </div>
  );
};

export default EggVariantSwitch;
